import React, { useRef, useState } from 'react'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import StatusCell from "./StatusCell"
import notFound from "../../../../components/NotFoundData"
import TableSkeletonRows from "../../../../components/Skeleton/TableSkeletonRows"
import EditPaymentModal from "../../../../components/PaymentModals/EditPaymentModal"
import ConfirmModal from "../../../../components/ConfirmModal/ConfirmModal"
import { changePaymentsProcess } from "../../../../features/payment/paymentSlice"
import { useDispatch } from 'react-redux'
import { showSnackBar } from "../../../../components/Snackbar/Snackbar"
import { formatMoney } from "../../../../utils/amount"
import { computeOwed, isPartiallyCollected } from "../../../../utils/partialPayment"
import { useMyContextLoading } from "../../../../components/LoadingContextProvider/LoadingContextProvider"
import { formatBusinessDate } from "../../../../utils/date"
import { NavLink } from "react-router-dom"
import { handleTableId } from "../../../../helpers/helper"
import PaymentPopover from "../../../Contacts/Table/Payment/PaymentPopover"
import GatewayPicker from "../../../../components/GatewayPicker/GatewayPicker"
import SortableTableHeader from "../../../../components/Table/SortableTableHeader"
import StatusBadge from "../../../../components/StatusBadge/StatusBadge"
import { LuMoreVertical, LuTrash2 } from "react-icons/lu"
import ProcessPaymentConfirm from "../../../../components/PaymentModals/ProcessPaymentConfirm"
import { useUiKit } from "../../../../components/UiKit/UiKitContext"
import { failureOf } from "../../../../utils/errorHelpers"

const headerCellSx = {
  fontSize: '13px', textTransform: 'uppercase', fontWeight: 600,
  color: 'text.secondary', backgroundColor: 'var(--color-bg-subtle)',
  borderBottom: 1, borderColor: 'divider',
  px: '10px', py: 0, verticalAlign: 'middle', whiteSpace: 'nowrap', lineHeight: 1,
}

const bodyCellSx = {
  fontSize: '0.875rem', fontWeight: 400,
  borderBottom: 1, borderColor: 'divider',
  color: 'text.primary', px: '10px', py: 0, verticalAlign: 'middle',
}

const TableList = ({ triggerUpdate = false, data = [], userRole, showTransactionalId = true, sortField = '', sortOrder = '', onSort }) => {
  const dispatch = useDispatch()
  const { confirm: kitConfirm } = useUiKit()
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [paymentId, setPaymentId] = useState('')
  const [curUserId, setCurUserId] = useState('')

  const handleClick = (event, id, userId) => {
    setCurUserId(userId)
    setPaymentId(id)
    setAnchorEl(event.currentTarget)
  }
  const { triggerLoading, isBarLoading } = useMyContextLoading()
  const showSkeleton = isBarLoading && !(data?.length > 0)

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  const formatBillingAccount = ({ isBankAccount, billingAccount, billingAccountLast4 }) => {
    if (isBankAccount) {
      if (billingAccount === 'Bank Account') {
        return `ACH **${billingAccountLast4} `
      } else if (billingAccount === 'Credit Card') {
        return `CC **${billingAccountLast4}`
      }
    } else {
      return "Missing"
    }
  }

  const capitalizeFirstLetter = (val) => {
    return String(val).charAt(0).toUpperCase() + String(val)?.slice(1)
  }

  const [openEdit, setOpenEdit] = useState(false)
  const [openChangeStatus, setOpenChangeStatus] = useState(false)
  // Per-charge gateway pick from the dialog's picker — override for THIS charge only, and
  // never carried to another payment: the picker clears it whenever it loads a new one, and
  // Confirm stays out of reach until this payment's own options have arrived.
  const [selectedGateway, setSelectedGateway] = useState(null)
  const [gatewayOptionsLoading, setGatewayOptionsLoading] = useState(true)

  const trigger = () => {
    triggerUpdate(true)
    setAnchorEl(null)
    setPaymentId('')
  }

  const payment = data?.find(el => el._id === paymentId)

  const chargeTarget = useRef(null)
  if (!openChangeStatus) chargeTarget.current = null
  else if (!chargeTarget.current && payment) chargeTarget.current = payment
  const target = chargeTarget.current || payment

  const processPayment = async () => {
    triggerLoading(true)
    try {
      let result = await dispatch(changePaymentsProcess({ paymentId: payment?._id, ...(selectedGateway || {}) }))
      if (result.error) {
        const errorMessage = result?.payload || 'An error occurred. Please try again.'
        showSnackBar("error", errorMessage)
      } else if (result?.payload?.data?._id) {
        showSnackBar("success", "The payment is being processed")
      }
      trigger()
    } catch (error) {
      showSnackBar('error', 'An unexpected error occurred. Please try again.')
    }
    triggerLoading(false)
  }

  const processPaymentOrThrow = async () => {
    const result = await dispatch(changePaymentsProcess({ paymentId: target?._id, ...(selectedGateway || {}) }))
    if (result.error) {
      triggerUpdate(true)
      setAnchorEl(null)
      throw failureOf(result, 'An error occurred. Please try again.')
    }
    if (result?.payload?.data?._id) showSnackBar("success", "The payment is being processed")
    trigger()
  }

  const NotFound = notFound

  const getColSpan = () => {
    // Contact Id, Date Due, User, Account Status, Assignees, Amount, Status
    let count = 7
    if (userRole === 'affiliate') count += 1
    if (userRole === 'customer' && showTransactionalId) count += 1
    if (userRole === 'customer') count += 3
    return count
  }

  return (
    <div>
      {openChangeStatus && kitConfirm && (
        <ProcessPaymentConfirm
          open={openChangeStatus}
          onClose={() => setOpenChangeStatus(false)}
          amount={formatMoney(computeOwed(target))}
          methodType={target?.billingAccount?.billingAccountType}
          last4={target?.billingAccount?.accountNumberLast4 || target?.billingAccount?.cardNumberLast4}
          confirmDisabled={gatewayOptionsLoading}
          onConfirm={processPaymentOrThrow}
        >
          <GatewayPicker paymentId={target?._id} onChange={setSelectedGateway} onLoadingChange={setGatewayOptionsLoading} />
          {target?.amountPaid > 0 && (
            <div className="text-text-secondary text-xs">
              Scheduled {formatMoney(target.amount)} — {formatMoney(target.amountPaid)} already collected
            </div>
          )}
        </ProcessPaymentConfirm>
      )}
      {openChangeStatus && !kitConfirm && <ConfirmModal leftTopIcon={<LuTrash2 size={18} className="text-text-secondary" />} title="Are you sure to process the Payment?"
        disabled={gatewayOptionsLoading}
        modalButtonTitle={"Confirm"} classes="" open={openChangeStatus}
        setOpen={setOpenChangeStatus} functionButton={processPayment}>
        <div>
          You will not be able to cancel the processing after you confirm this alert
        </div>
        <br />
        <div>
          Billing Type: {payment?.billingAccount?.billingAccountType}
        </div>
        <div>
          {payment?.billingAccount?.billingAccountType === 'Bank Account' ? 'Account' : 'Card'} Number:
          {payment?.billingAccount?.accountNumberLast4 || payment?.billingAccount?.cardNumberLast4}
        </div>
        <br />
        <GatewayPicker paymentId={payment?._id} onChange={setSelectedGateway} onLoadingChange={setGatewayOptionsLoading} />
        <div>
          Amount: {payment?.amount}$
        </div>
      </ConfirmModal>}
      {openEdit && <EditPaymentModal triggerUpdate={trigger} curUserId={curUserId}
        dataRow={{
          id: payment?._id,
          dueDate: payment?.dueDate,
          amount: payment?.amount,
          status: payment?.status?._id,
          transactionId: payment?.transactionId,
          billingAccount: payment?.billingAccountId
        }}
        open={openEdit}
        onClose={setOpenEdit} idPayment={paymentId} />}
      <TableContainer sx={{ minHeight: data?.length === 0 ? 300 : undefined }}>
        <Table>
          <TableHead>
            <TableRow>
              {userRole === 'affiliate' && (
                <TableCell sx={headerCellSx}>Pmt #</TableCell>
              )}
              <TableCell sx={headerCellSx}>Contact Id</TableCell>
              <TableCell sx={{ ...headerCellSx, p: 0 }}>
                <SortableTableHeader
                  label="Date Due"
                  field="dueDate"
                  currentSortField={sortField}
                  currentSortOrder={sortOrder}
                  onSort={onSort}
                  sortable={!!onSort}
                />
              </TableCell>
              <TableCell sx={headerCellSx}>User</TableCell>
              <TableCell sx={headerCellSx}>Account Status</TableCell>
              <TableCell sx={headerCellSx}>Assignees</TableCell>
              <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Amount</TableCell>
              <TableCell sx={headerCellSx}>Status</TableCell>
              {userRole === 'customer' && showTransactionalId && (
                <TableCell sx={headerCellSx}>Transaction ID</TableCell>
              )}
              {userRole === 'customer' && (
                <>
                  <TableCell sx={headerCellSx}>Plan Status</TableCell>
                  <TableCell sx={headerCellSx}>Billing Account</TableCell>
                  <TableCell sx={headerCellSx}>Action</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {showSkeleton ? (
              <TableSkeletonRows rows={8} colSpan={getColSpan()} />
            ) : data?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={getColSpan()} sx={{ p: 0, border: 'none' }}>
                  <NotFound />
                </TableCell>
              </TableRow>
            ) : (
              data?.map((row) => (
                <TableRow
                  key={row._id}
                  sx={{ height: 75 }}
                  className={row?.status?.label === 'Failed' ? 'bg-error-light' : ''}
                >
                  {userRole === 'affiliate' && (
                    <TableCell sx={bodyCellSx}>
                      <div className="w-full text-wrap line-clamp-3">
                        {row?.pmt}
                      </div>
                    </TableCell>
                  )}
                  <TableCell sx={bodyCellSx}>
                    <div className="pl-5 py-4 text-primary-main font-semibold cursor-pointer">
                      {row?.user ? (
                        <NavLink to={`/contact/${row?.user?._id}`} className="cursor-pointer">
                          {handleTableId(row?.user?._id)}
                        </NavLink>
                      ) : ' - '}
                    </div>
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {formatBusinessDate(row?.dueDate)}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {row?.user ? `${row?.user?.firstName} ${row?.user?.lastName}` : ' - '}
                  </TableCell>
                  {/* The customer's own status, not the payment's. Accounting needs to see
                      at a glance whether the account behind a receivable is Active,
                      Retention, Cancelled and so on, without opening each contact. The
                      API already returns it and the CSV export already carries it as
                      "Customer Status"; only this table was missing it. */}
                  <TableCell sx={bodyCellSx}>
                    {row?.userStatus?.label || ' - '}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {(() => {
                      const assignees = (row?.assignees || [])
                        .map((assignee) => ({
                          id: assignee?._id,
                          name: `${assignee?.firstName || ''} ${assignee?.lastName || ''}`.trim(),
                        }))
                        .filter((assignee) => assignee.name)
                      return assignees.length > 0 ? (
                        <div className="flex flex-col">
                          {assignees.map((assignee, index) => (
                            <span key={assignee.id ?? index}>{assignee.name}</span>
                          ))}
                        </div>
                      ) : ' - '
                    })()}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
                    {formatMoney(row?.amount)}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <StatusCell props={{ row, formattedValue: row?.status }} userRole={userRole} />
                    {row?.settledByPayment && (
                      <span
                        className="mt-1 inline-block px-2 py-0.5 rounded-full bg-success-light text-success-main text-xs"
                        title={`Settled later by payment ${handleTableId(row?.settledByPayment)}`}
                      >
                        Settled later
                      </span>
                    )}
                    {Array.isArray(row?.settlesPayments) && row?.settlesPayments.length > 0 && (
                      <span
                        className="mt-1 inline-block px-2 py-0.5 rounded-full bg-info-light text-info-main text-xs"
                        title={`Settles ${row?.settlesPayments.length} scheduled payment(s)`}
                      >
                        Settles {row?.settlesPayments.length}
                      </span>
                    )}
                    {isPartiallyCollected({ ...row, status: row?.status?.value }) && (
                      <>
                        <span
                          className="mt-1 inline-block px-2 py-0.5 rounded-full bg-warning-light text-warning-main text-xs"
                          title={`${formatMoney(row.amountPaid)} already collected by a payment link`}
                        >
                          Partially paid
                        </span>
                        <div className="text-text-secondary text-xs">
                          {formatMoney(computeOwed(row))} of {formatMoney(row.amount)}
                        </div>
                      </>
                    )}
                  </TableCell>
                  {userRole === 'customer' && showTransactionalId && (
                    <TableCell sx={bodyCellSx}>
                      <div
                        className="w-full text-wrap line-clamp-3"
                        title={row?.transactionId || row?.transactionData?.reasonText || ''}
                      >
                        {row?.transactionId || row?.transactionData?.reasonText || ''}
                      </div>
                    </TableCell>
                  )}
                  {userRole === 'customer' && (
                    <>
                      <TableCell sx={bodyCellSx}>
                        {(() => {
                          // No plan is an absence, not an error — keep it neutral so the
                          // table doesn't read as a wall of red.
                          // Canceled is a settled end state, not a fault, so it reads neutral
                          // too. Red stays for inactive, the one an operator may still act on.
                          const planStatus = row?.plan?.status
                          const tone = !planStatus || planStatus === 'canceled'
                            ? { bg: 'var(--color-gray-200)', text: 'var(--color-gray-600)' }
                            : planStatus === 'active'
                              ? { bg: 'var(--color-success-background)', text: 'var(--color-success-main)' }
                              : { bg: 'var(--color-error-background)', text: 'var(--color-error-main)' }
                          return (
                            <StatusBadge
                              label={capitalizeFirstLetter(planStatus || 'N/A')}
                              bgColor={tone.bg}
                              textColor={tone.text}
                            />
                          )
                        })()}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        {(() => {
                          const billingAccount = row?.billingAccount
                          const isBankAccount = !!billingAccount?.billingAccountType
                          return (
                            <div className={!isBankAccount ? 'text-error-main' : ''}>
                              {formatBillingAccount({
                                isBankAccount,
                                billingAccount: billingAccount?.billingAccountType,
                                billingAccountLast4: billingAccount?.accountNumberLast4 || billingAccount?.cardNumberLast4
                              })}
                            </div>
                          )
                        })()}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <div className="w-full">
                          <div
                            aria-describedby={id}
                            variant="contained"
                            onClick={(event) => handleClick(event, row?._id, row?.user?._id)}
                            className="cursor-pointer w-full flex justify-center"
                          >
                            <LuMoreVertical size={20} style={{ color: 'var(--color-gray-500)' }} />
                          </div>
                        </div>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <PaymentPopover paymentId={payment?._id}
        data={data}
        anchorEl={anchorEl}
        id={id}
        handleClose={handleClose}
        open={open}
        setOpenChangeStatus={setOpenChangeStatus}
        setOpenEdit={setOpenEdit}
        trigger={trigger} />
    </div>
  )
}

export default TableList
